import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectionList } from '@angular/material/list';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { fadeInAndOut } from '~lmo/animations';
import { LmoOrdersCancelDialogComponent } from '~lmo/components';
import {
  inProgressStatusIndexKey,
  isEtaArrived,
  isEtaLoading,
  isEtaNone,
  isEtaStaging,
  isEtaUnloading,
  MeshWithOrders,
  Order,
  OrdersGroupedWithInProgressStatus,
} from '~lmo/models/order.model';
import { isUnreachable } from '~lmo/models/user.model';
import { loadPluralMapping } from '~lmo/pluralMappings';
import { LmoOrdersService } from '~lmo/services';
import { HelperService } from '~services/helper.service';

const GROUP_BY_DESTINATION = 'group_by_destination';

@Component({
  animations: [fadeInAndOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-in-progress',
  styleUrls: ['./lmo-orders-in-progress.component.scss'],
  templateUrl: './lmo-orders-in-progress.component.html',
})
export class LmoOrdersInProgressComponent implements OnInit {
  public isEtaNone = isEtaNone;
  public isEtaArrived = isEtaArrived;
  public isEtaLoading = isEtaLoading;
  public isEtaUnloading = isEtaUnloading;
  public isEtaStaging = isEtaStaging;
  public loadPluralMapping = loadPluralMapping;
  public inProgressOrders$: Observable<OrdersGroupedWithInProgressStatus[] | MeshWithOrders[]>;
  public isUnreachable = isUnreachable;
  public groupByDestination$$ = new BehaviorSubject<boolean>(JSON.parse(localStorage.getItem(GROUP_BY_DESTINATION)));
  public byDestinationView$: Observable<boolean>;
  @ViewChild('selectedOrderIds', { static: false }) private selectedOrders: MatSelectionList;

  constructor(
    private orderService: LmoOrdersService,
    public helperService: HelperService,
    private matDialog: MatDialog,
  ) {
    const isGroupByDestination: boolean = this.getLocalStorageGroupByDest();
    this.loadData(isGroupByDestination);
  }

  public loadData(isGroupByDestination: boolean) {
    this.inProgressOrders$ = isGroupByDestination
      ? this.orderService.inProgress$.pipe(map(groupByDestination))
      : this.orderService.inProgress$.pipe(map(groupByMeshes));
  }

  public ngOnInit() {
    this.byDestinationView$ = this.groupByDestination$();
  }

  public groupByDestination$(): Observable<boolean> {
    return this.groupByDestination$$.asObservable().pipe(debounceTime(1));
  }

  public trackByIdFn(_index: number, order: { id: number }): number {
    return order.id;
  }

  public getLocalStorageGroupByDest(): boolean {
    return JSON.parse(localStorage.getItem(GROUP_BY_DESTINATION));
  }

  public cancelUnloadedOrders() {
    const canceller = this.matDialog.open(LmoOrdersCancelDialogComponent);
    canceller.componentInstance.orderIds = this.selectedOrders.selectedOptions.selected.map(
      (option) => option.value.id,
    );
    canceller.afterClosed().subscribe((wasCancelled) => {
      if (wasCancelled) {
        this.selectedOrders.deselectAll();
      }
    });
  }

  public conditionalValueByGroup(byMeshValue: string | null, byDestValue: string | null): string | null {
    if (this.getLocalStorageGroupByDest()) {
      return byDestValue;
    } else {
      return byMeshValue;
    }
  }

  public toggleGroupByDestination($event: Event, checked: boolean) {
    this.groupByDestination$$.next(checked);
    localStorage.setItem(GROUP_BY_DESTINATION, JSON.stringify(checked));
    this.loadData(checked);
  }

  public toggleOrderGroupChecks(checked: MatCheckboxChange, key: number | string) {
    if (this.getLocalStorageGroupByDest()) {
      return this.toggleDestinationChecks(checked, key as string);
    } else {
      return this.toggleMeshChecks(checked, key as number);
    }
  }

  public toggleMeshChecks(checked: MatCheckboxChange, meshId: number) {
    const meshMatches = this.selectedOrders.options.filter((option) => {
      const order = option.value as Order;
      return order.mesh.id === meshId;
    });
    meshMatches.forEach((option) => {
      if (option.selected !== checked.checked) {
        option.toggle();
      }
    });
  }

  public toggleDestinationChecks(checked: MatCheckboxChange, status: string) {
    const index = inProgressStatusIndexKey()[status];
    const orderIDs = [];
    this.inProgressOrders$.subscribe((groups) => orderIDs.push(...groups[index].orders.map((order) => order.id)));

    const orderMatches = this.selectedOrders.options.filter((option) => {
      const order = option.value as Order;
      return orderIDs.find((id) => id === order.id);
    });
    orderMatches.forEach((option) => {
      if (option.selected !== checked.checked) {
        option.toggle();
      }
    });
  }

  public cancelOrders() {
    const canceller = this.matDialog.open(LmoOrdersCancelDialogComponent, {
      panelClass: ['w-full', 'md:w-1/2', 'lg:w-1/3'],
    });
    canceller.componentInstance.orderIds = this.selectedOrders.selectedOptions.selected.map(
      (option) => option.value.id,
    );
    canceller.componentInstance.skipLoaded = true;
    canceller.afterClosed().subscribe((wasCancelled) => {
      if (wasCancelled) {
        this.selectedOrders.deselectAll();
      }
    });
  }

  public trackByFnReason(index, item) {
    return item;
  }

  public checkBolMissingDetails(order: Order): string[] {
    const reasons = [];

    if (!order.bolNumber) {
      reasons.push('Missing BOL number');
    }

    const bolImage = order.uploadedFiles?.find((file) => file.category === 'bol_image');
    if (!bolImage) {
      reasons.push('Missing BOL ticket upload');
    }
    return reasons;
  }
}

function groupByMeshes(orders: Order[]): MeshWithOrders[] {
  const meshMap: Record<number, MeshWithOrders> = {};
  orders.forEach((order) => {
    if (!meshMap[order.mesh.id]) {
      meshMap[order.mesh.id] = {
        ...order.mesh,
        orders: [],
      };
    }
    meshMap[order.mesh.id].orders.push(order);
  });
  const meshWithOrdersArray: MeshWithOrders[] = Object.keys(meshMap).map((key) => meshMap[key]);
  meshWithOrdersArray.sort((m1, m2) => (m1.preferredOrder < m2.preferredOrder ? -1 : 1));
  meshWithOrdersArray.forEach((meshWithOrders) => {
    meshWithOrders.orders.sort(
      (o1, o2) =>
        sortFn(o1.estimateDeliveryTimestamp, o2.estimateDeliveryTimestamp) || sortFn(o1.loadNumber, o2.loadNumber),
    );
  });
  return meshWithOrdersArray;
}

function groupByDestination(orders: Order[]): OrdersGroupedWithInProgressStatus[] {
  const statusMap: Record<string, OrdersGroupedWithInProgressStatus> = {
    dispatched: {
      id: 1,
      inProgressStatus: 'Sent to Driver',
      orders: [],
    },
    driver_accepted_loaded: { id: 3, inProgressStatus: 'To Well', orders: [] },
    driver_accepted_unloaded: { id: 2, inProgressStatus: 'To Loader', orders: [] },
  };

  orders.forEach((order: Order) => {
    if (order.orderStatus === 'dispatched') {
      statusMap['dispatched'].orders.push(order);
    } else if (order.orderStatus === 'driver_accepted') {
      if (order.loaded) {
        statusMap['driver_accepted_loaded'].orders.push(order);
      } else {
        statusMap['driver_accepted_unloaded'].orders.push(order);
      }
    }
  });
  const statusWithOrdersArray: OrdersGroupedWithInProgressStatus[] = Object.keys(statusMap).map(
    (key) => statusMap[key],
  );
  statusWithOrdersArray.sort((m1, m2) => (m1.id < m2.id ? -1 : 1));
  statusWithOrdersArray.forEach((statusWithOrders) => {
    statusWithOrders.orders.sort(
      (o1, o2) =>
        sortFn(o1.eta, o2.eta) ||
        sortFn(o1.estimateDeliveryTimestamp, o2.estimateDeliveryTimestamp) ||
        sortFn(o1.loadNumber, o2.loadNumber),
    );
  });

  return statusWithOrdersArray;
}

function sortFn(a: any, b: any): number {
  return a === b ? 0 : a < b ? -1 : 1;
}
